import { assign, EventObject, StateNodeConfig, StateSchema } from 'xstate';
import queryOrder from '../networkRequests/order/queryOrder';
import { OrderIdMachineContext } from './types/shared/context/orderIdMachineContext';
import { PropagateNetworkErrors } from './actions/genericActions';
import { OrderMachineContext } from './types/shared/context/orderMachineContext';
import { CustomerSearchResponseMachineContext } from './types/shared/context/customerSearchMachineContext';
import { CellPhoneMachineContext } from './types/shared/context/cellPhoneMachineContext';
import { isValidAddress } from '../helpers/validators/address.helper';
import queryOrderWithCartValidation from '../networkRequests/order/queryOrderWithCartValidation';
import { setAppStatusParams } from '../helpers/url/url.helpers';

export const MakeStateCallingQueryOrder = <
  TContext extends OrderIdMachineContext & OrderMachineContext,
  TStateSchema extends StateSchema,
  TEvent extends EventObject,
>(
  onDoneState: string,
  onErrorState?: string,
): StateNodeConfig<TContext, TStateSchema, TEvent> => {
  return {
    tags: 'calling',
    invoke: {
      src: queryOrder,
      onDone: {
        target: onDoneState,
        actions: [
          assign((_context: any, event: any) => ({
            application: event.data?.application,
            dealer: event.data?.dealer,
            orderDetails: event.data?.details,
          })),
          (context) => {
            setAppStatusParams(context?.application?.lease?.status);
          },
        ],
      },
      onError: { target: onErrorState, actions: PropagateNetworkErrors },
    },
  };
};

// this one requests the cartValidationAdditionalDetails field, which eventually calls the llm
export const MakeStateCallingQueryOrderWithCartValidation = <
  TContext extends OrderIdMachineContext & OrderMachineContext,
  TStateSchema extends StateSchema,
  TEvent extends EventObject,
>(
  onDoneState: string,
  onErrorState?: string,
): StateNodeConfig<TContext, TStateSchema, TEvent> => {
  return {
    tags: 'calling',
    invoke: {
      src: queryOrderWithCartValidation,
      onDone: {
        target: onDoneState,
        actions: [
          assign((_context: any, event: any) => ({
            application: event.data?.application,
            dealer: event.data?.dealer,
            orderDetails: event.data?.details,
          })),
          (context) => {
            setAppStatusParams(context?.application?.lease?.status);
          },
        ],
      },
      onError: { target: onErrorState, actions: PropagateNetworkErrors },
    },
  };
};

// this one assigns basic info, etc fields from order props
export const MakeStateCallingQueryOrderPreAuth = <
  TContext extends OrderIdMachineContext & OrderMachineContext & CustomerSearchResponseMachineContext & CellPhoneMachineContext,
  TStateSchema extends StateSchema,
  TEvent extends EventObject,
>(
  onDoneState: string,
  onErrorState?: string,
): StateNodeConfig<TContext, TStateSchema, TEvent> => {
  return {
    tags: 'calling',
    invoke: {
      src: queryOrder,
      onDone: {
        target: onDoneState,
        actions: assign((context: TContext, event: any) => {
          const assignObject: any = {
            application: event.data?.application,
            dealer: event.data?.dealer,
            orderDetails: event.data?.details,
          };

          // on app load, fill application form state from order response if possible,
          // only doing so if it will not overwrite existing data.
          if (!context.basicInfo || Object.values(context.basicInfo).every((val) => !val)) {
            assignObject.basicInfo = {
              firstName: event.data?.application?.customer?.firstName ?? '',
              lastName: event.data?.application?.customer?.lastName ?? '',
              emailAddress: event.data?.application?.customer?.emailAddress ?? '',
            };
          }

          if (!context.basicInfoContinued || Object.entries(context.basicInfoContinued).every(([, value]) => !value)) {
            assignObject.basicInfoContinued = {
              ...context.basicInfoContinued, // reuse tax id if we have it, this is a functional change
              birthDate: event.data?.application?.customer?.birthDate ?? '',
              tokenizedTaxId: event.data?.application?.customer?.tokenizedTaxId ?? '',
              monthlyIncome: event.data?.application?.lease?.employment?.monthlyIncome ?? '',
            };
          }

          if (!context.cellPhone) {
            assignObject.cellPhone = event.data?.application?.customer?.cellPhone ?? '';
          }

          if (!isValidAddress(context.accommodationAddress)) {
            assignObject.accommodationAddress = {
              line1: event.data?.application?.customer?.accommodationAddress?.line1 ?? '',
              line2: event.data?.application?.customer?.accommodationAddress?.line2 ?? '',
              city: event.data?.application?.customer?.accommodationAddress?.city ?? '',
              state: event.data?.application?.customer?.accommodationAddress?.state ?? '',
              zip: event.data?.application?.customer?.accommodationAddress?.zip ?? '',
            };
          }

          if (!isValidAddress(context.billingAddress)) {
            assignObject.billingAddress = {
              line1: event.data?.application?.customer?.billingAddress?.line1 ?? '',
              line2: event.data?.application?.customer?.billingAddress?.line2 ?? '',
              city: event.data?.application?.customer?.billingAddress?.city ?? '',
              state: event.data?.application?.customer?.billingAddress?.state ?? '',
              zip: event.data?.application?.customer?.billingAddress?.zip ?? '',
            };
          }

          if (
            !context.payFrequency ||
            Object.entries(context.payFrequency)
              .filter(([key]) => key !== 'nextPayDate' && key !== 'isPaidViaDirectDeposit') // exclude nullable fields
              .every(([, value]) => !value)
          ) {
            assignObject.payFrequency = {
              paymentFrequency: event.data?.application?.lease?.employment?.payPeriod ?? '',
              lastPayDate: event.data?.application?.lease?.employment?.lastPayDate ?? '',
              nextPayDate: event.data?.application?.lease?.employment?.nextPayDate ?? '',
              isPaidViaDirectDeposit: event.data?.application?.lease?.employment?.isPaidViaDirectDeposit ?? false,
            };
          }

          return assignObject;
        }),
      },
      onError: { target: onErrorState, actions: PropagateNetworkErrors },
    },
  };
};
